/* =========================================================================
   GTKY conversation moment (registry moment 'gtky'). A reusable, self-contained
   Theo-conversation card: it frames one Get-To-Know-You question, caches the
   answer to the local memory cache (workstream I), and emits GtkyAnswered. Later
   waves (Journey Director / feast flow) invoke triggerGtkyMoment() at the right
   beats; it is fully guest-safe.

   AUTHED server sync is WIRED (v1.1.0, PLAN-gameplay-v2 §6 phase 1): a real
   answer from a signed-in player is mirrored to POST /api/gtky/submit via
   api.submitGtky(). It is deliberately fire-and-forget — localStorage stays the
   LOCAL source of truth that conditioned character lines read, so a guest, an
   offline device or a 404 on the key costs the player nothing.

   RESPECT (§6): every question carries "Prefer not to say", which stores
   GTKY_DECLINE_SENTINEL so the question is retired for good — never re-asked,
   never mirrored to the server, and never used to condition a Theo line
   (registry.ts treats the sentinel as unsatisfied). "Maybe later" stores
   nothing: the Director's 50-answer mark was stamped when the moment was
   granted, so the same question simply comes back after the next 50.
   ========================================================================= */
import { byId } from './dom';
import { esc } from './engine';
import { track } from './telemetry';
import { setGuestName, isAuthenticated, submitGtky } from './api';
import { rememberGtky, recallGtky, GTKY_DECLINE_SENTINEL } from './characters/memory';
import { getLine } from './characters/registry';
import { sanitizeName, NAME_SKIP_SENTINEL, PLAYER_NAME_KEY } from './characters/name';
import { GTKY_STARTER, type GtkyQuestion } from './data/gtkyData';
import { markAskedToday, unansweredKeys } from './gtkyCadence';

const THEO_EMOJI = '🧑‍🦲';
/** Conversation-header portrait slot — Eric's Theo headshot lands here with zero
    code edits (same probe pattern as initCharacterArt; emoji fallback until).
    NOTE: probed here, not in characters/ui.ts — ui.ts consumes gtkyOverlayHTML at
    module-eval time, so importing back into this file would risk a TDZ cycle. */
const HEADSHOT_PATH = 'media/characters/theo-headshot.png';
let headshotAvail = false;
let headshotProbed = false;

function probeHeadshot(): void {
  if (headshotProbed) return;
  headshotProbed = true;
  try {
    const img = new Image();
    img.onload = () => {
      headshotAvail = true;
      fillBadge(); // art landed while (or before) the overlay is up — swap it in
    };
    img.onerror = () => {
      /* asset not present yet — emoji fallback stays */
    };
    img.src = HEADSHOT_PATH;
  } catch {
    /* no Image() (tests) — emoji fallback */
  }
}

/** Render the overlay's header portrait: headshot art when available, else the
    emoji. Shared by the name-ask and regular GTKY conversation variants. */
function fillBadge(): void {
  const badge = document.getElementById('gtkyBadge');
  if (!badge) return;
  badge.innerHTML = headshotAvail
    ? `<img class="gtky-badge-img" src="${HEADSHOT_PATH}" alt="Brother Theo" />`
    : `<span class="gtky-badge-emoji">${THEO_EMOJI}</span>`;
}

export const gtkyOverlayHTML = /* html */ `
<div id="gtkyOverlay" class="gtky hidden" aria-hidden="true">
  <div class="gtky-dim"></div>
  <div class="gtky-card">
    <div class="gtky-badge" id="gtkyBadge"><span class="gtky-badge-emoji">${THEO_EMOJI}</span></div>
    <div class="gtky-kicker" id="gtkyKicker"></div>
    <div class="gtky-prompt" id="gtkyPrompt"></div>
    <div class="gtky-controls" id="gtkyControls"></div>
    <button class="gtky-skip" id="gtkySkip" type="button">Maybe later</button>
  </div>
</div>`;

/** The next starter question with no cached answer yet, or null when all
    answered/declined. The special 'player_name' ask is excluded — it's driven by
    triggerNameAsk(), not the generic GTKY rotation. Bank order IS ask order
    (T1 warm → T2 engagement → T3 spiritual-life-lite; see gtkyData.ts). */
export function pickGtkyQuestion(): GtkyQuestion | null {
  for (const q of GTKY_STARTER) {
    if (q.key === PLAYER_NAME_KEY) continue;
    if (recallGtky(q.key) == null) return q;
  }
  return null;
}

/** How many questions are still worth asking (0 → the system goes quiet). */
export function gtkyPoolRemaining(): number {
  return unansweredKeys(GTKY_STARTER, recallGtky, PLAYER_NAME_KEY).length;
}

/* ------------------------- first-name ask (v1.1.0) ------------------------- */

/** True once the player has answered OR skipped the name ask (never re-ask). */
export function nameAsked(): boolean {
  return recallGtky(PLAYER_NAME_KEY) != null;
}

/** Show Theo's warm first-name ask through the shared GTKY overlay. Guest-safe;
    caches to memory['player_name'] on answer (or a skip sentinel on decline), so
    it is asked at most once. On a real answer: forwards the name to the guest
    telemetry channel (setGuestName) and emits GuestNamed (no name in props). */
export function triggerNameAsk(done?: () => void): void {
  if (nameAsked()) {
    if (done) done();
    return;
  }
  onDone = done ?? null;

  probeHeadshot();
  fillBadge();
  byId('gtkyKicker').textContent = getLine('theo', 'gtky');
  byId('gtkyPrompt').textContent = 'Before we set out — what should I call you?';

  const controls = byId('gtkyControls');
  controls.innerHTML = '';
  const input = document.createElement('input');
  input.className = 'gtky-input';
  input.type = 'text';
  input.maxLength = 20;
  input.autocomplete = 'given-name';
  input.placeholder = 'First name or nickname…';
  const btn = document.createElement('button');
  btn.className = 'btn gtky-send';
  btn.type = 'button';
  btn.textContent = 'Tell Theo';
  btn.onclick = () => finishNameAsk(input.value);
  input.onkeydown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      finishNameAsk(input.value);
    }
  };
  controls.appendChild(input);
  controls.appendChild(btn);

  const skip = byId('gtkySkip');
  skip.textContent = 'Skip';
  skip.onclick = () => {
    // Store a sentinel so we never re-ask; no name → no personalization.
    rememberGtky(PLAYER_NAME_KEY, NAME_SKIP_SENTINEL);
    skip.textContent = 'Maybe later'; // restore default for later GTKY moments
    close();
  };

  const ov = byId('gtkyOverlay');
  ov.classList.remove('hidden');
  ov.setAttribute('aria-hidden', 'false');
  void ov.offsetWidth;
  ov.classList.add('show');
}

function finishNameAsk(raw: string): void {
  const clean = sanitizeName(raw);
  const skip = byId('gtkySkip');
  skip.textContent = 'Maybe later'; // restore default for later GTKY moments
  if (!clean) {
    // Nothing usable typed → treat like a skip (store sentinel, never re-ask).
    rememberGtky(PLAYER_NAME_KEY, NAME_SKIP_SENTINEL);
    close();
    return;
  }
  rememberGtky(PLAYER_NAME_KEY, clean);
  if (!isAuthenticated()) setGuestName(clean); // ride the next guest-activity batch
  track('GuestNamed'); // name travels only in the dedicated field, never in props
  close();
}

/** Cache the answer locally, emit telemetry, and (authed only) mirror it into
    the server bank. The mirror is fire-and-forget: the local cache is what the
    game reads, so a failed POST is invisible to the player by design. */
export function recordGtkyAnswer(key: string, value: string): void {
  rememberGtky(key, value);
  track('GtkyAnswered', { key });
  if (isAuthenticated()) void submitGtky(key, value);
}

/** "Prefer not to say": retire the question for good. Stored locally only — a
    decline is not an answer, so nothing is mirrored to the server bank. */
export function recordGtkyDecline(key: string): void {
  rememberGtky(key, GTKY_DECLINE_SENTINEL);
  track('GtkySkipped', { key, reason: 'declined' });
}

let onDone: (() => void) | null = null;

function close(): void {
  const ov = byId('gtkyOverlay');
  ov.classList.add('hidden');
  ov.classList.remove('show');
  ov.setAttribute('aria-hidden', 'true');
  const done = onDone;
  onDone = null;
  if (done) done();
}

function submit(q: GtkyQuestion, value: string): void {
  if (value.trim().length === 0) return;
  recordGtkyAnswer(q.key, value);
  close();
}

/** Show a GTKY moment for the given question (defaults to the next unanswered).
    Renders answer controls per answerType. Guest-safe; no-op when nothing left. */
export function triggerGtkyMoment(done?: () => void, question?: GtkyQuestion | null): void {
  const q = question ?? pickGtkyQuestion();
  if (!q) {
    if (done) done();
    return;
  }
  onDone = done ?? null;
  // An ask spends the day's single slot the moment it is SHOWN — answered,
  // declined or dismissed, we do not come back today (§6 caps & respect).
  markAskedToday();
  track('GtkyAsked', { key: q.key });
  probeHeadshot();
  fillBadge();
  byId('gtkyKicker').textContent = getLine('theo', 'gtky');
  byId('gtkyPrompt').textContent = q.prompt;

  const controls = byId('gtkyControls');
  controls.innerHTML = '';

  if (q.answerType === 'text') {
    const input = document.createElement('input');
    input.className = 'gtky-input';
    input.type = 'text';
    input.maxLength = 60;
    input.placeholder = 'Type your answer…';
    const btn = document.createElement('button');
    btn.className = 'btn gtky-send';
    btn.type = 'button';
    btn.textContent = 'Tell Theo';
    btn.onclick = () => submit(q, input.value.trim());
    controls.appendChild(input);
    controls.appendChild(btn);
  } else if (q.answerType === 'bool') {
    for (const label of ['Yes', 'No']) {
      const b = document.createElement('button');
      b.className = 'gtky-opt';
      b.type = 'button';
      b.textContent = label;
      b.onclick = () => submit(q, label);
      controls.appendChild(b);
    }
  } else if (q.answerType === 'multi') {
    const chosen = new Set<string>();
    (q.options || []).forEach((opt) => {
      const b = document.createElement('button');
      b.className = 'gtky-opt';
      b.type = 'button';
      b.textContent = opt;
      b.onclick = () => {
        if (chosen.has(opt)) {
          chosen.delete(opt);
          b.classList.remove('picked');
        } else {
          chosen.add(opt);
          b.classList.add('picked');
        }
      };
      controls.appendChild(b);
    });
    const send = document.createElement('button');
    send.className = 'btn gtky-send';
    send.type = 'button';
    send.textContent = 'Tell Theo';
    send.onclick = () => submit(q, Array.from(chosen).join(', '));
    controls.appendChild(send);
  } else {
    // 'single'
    (q.options || []).forEach((opt) => {
      const b = document.createElement('button');
      b.className = 'gtky-opt';
      b.type = 'button';
      b.innerHTML = esc(opt);
      b.onclick = () => submit(q, opt);
      controls.appendChild(b);
    });
  }

  // "Prefer not to say" sits on EVERY question (§6 reverence policy) — a quiet
  // tertiary control under the answers, never styled like an answer.
  const decline = document.createElement('button');
  decline.className = 'gtky-decline';
  decline.type = 'button';
  decline.textContent = 'Prefer not to say';
  decline.onclick = () => {
    recordGtkyDecline(q.key);
    close();
  };
  controls.appendChild(decline);

  const skip = byId('gtkySkip');
  skip.textContent = 'Maybe later'; // may have been changed by the name ask
  skip.onclick = () => {
    // Nothing stored: the Director already stamped the 50-answer mark, so this
    // exact question returns after the next 50 — "ask me later", honoured.
    track('GtkySkipped', { key: q.key, reason: 'later' });
    close();
  };

  const ov = byId('gtkyOverlay');
  ov.classList.remove('hidden');
  ov.setAttribute('aria-hidden', 'false');
  void ov.offsetWidth;
  ov.classList.add('show');
}
